/**
 * clocksync.ts — the client clock controller (M69, rebuilt M143). PURE: no
 * timers, no sockets — the driver feeds it samples and asks it what to do.
 *
 * WHY A CLIENT CLOCK AT ALL. The server applies each input at the tick it is
 * labelled FOR (scheduled inputs — AuthoritativeRoom). So the client's tick
 * counter must run AHEAD of the server's by a little more than the time an
 * input needs to get there; then an input labelled "tick T" is waiting in the
 * server's queue when the server simulates T. That lead is the game's INPUT
 * BUFFER: too small and controls arrive late (the server holds a stale one —
 * "my steering lagged"), too large and the client predicts further into the
 * future than it needs to (bigger corrections when something unpredictable
 * happens). The same model as Rocket League and Overwatch.
 *
 * THE SIGNAL. Every snapshot reports the MARGIN: over the ticks since the last
 * snapshot, the least the client's newest input was ahead of the server
 * (negative = an input was late).
 *
 * WHAT WAS WRONG BEFORE (measured on real Wi-Fi and in the chaos rig):
 *  1. A fixed 5-tick target. A Mac or iPhone on Wi-Fi goes off-channel for up
 *     to ~100 ms about twice a second (AWDL); with 42 ms in hand every one of
 *     those hops delivered late controls and a visible correction.
 *  2. An average (EMA) as the estimator. Delay only ever makes inputs LATER,
 *     so the average says little about the arrivals that matter — the worst.
 *  3. "Hard resyncs" that wiped the prediction history, and that re-triggered
 *     on feedback measured BEFORE the resync (a cascade of 2–4 per event).
 *
 * WHAT THIS DOES INSTEAD — an adaptive jitter buffer, the standard answer:
 *  - It keeps the last ~10 s of margins and steers by the WORST one, so the
 *    lead grows exactly as far as this link's bad moments need (and no
 *    further than JITTER_CAP beyond the typical arrival — a radio blackout is
 *    not jitter and is not chased).
 *  - It grows at once and gives back slowly (attack fast, release slow).
 *  - It never acts on feedback that predates its own last action: a sample
 *    counts only once the server has SEEN an input sent after that action
 *    (snapshot tick + margin ≥ the first label after it). No cascades — by
 *    construction, not by timer.
 *  - Its only two actions are "simulate n extra ticks now" and "skip one
 *    tick". Tick labels therefore only ever move FORWARD: the server never
 *    has to guess whether a client rewound its clock, because none does.
 */

import { clamp } from '@/math/angles';
import { SNAPSHOT_INTERVAL_TICKS } from '@/net/protocol';

/** Ticks the worst recent arrival should still have in hand (~17 ms). */
export const SAFE_MARGIN = 2;
/** The most lead the buffer may add on top of the TYPICAL arrival to cover
 *  jitter (16 ticks ≈ 133 ms — sized to Apple's measured ~95 ms Wi-Fi hop
 *  plus the 4-tick batch interval). Deeper dips are outages, not jitter. */
export const JITTER_CAP = 16;
/** How long a bad arrival is remembered (ms). */
export const WINDOW_MS = 10_000;
/** Give lead back only once the worst arrival has this much to spare. */
export const RELEASE_HYSTERESIS = 3;
/** Ticks between give-backs (24 → at most 5 skipped ticks a second). */
export const RELEASE_COOLDOWN = 24;
/** …unless the clock is far ahead: then drain faster. */
export const FAR_AHEAD = 24;
export const RELEASE_COOLDOWN_FAR = 2;
/** The most ticks one action may add (any remainder follows on the next tick,
 *  from the same estimate). */
export const MAX_ADVANCE = 60;
/** LOCAL STALLS (see {@link ClockSync.stalled}). Stalls less than this far
 *  apart are a RUN; from the CHRONIC_RUN-th in a row the device is simply
 *  slow (every frame overruns), and its bursty sending is something the
 *  buffer must learn — not noise to be ignored. */
export const CHRONIC_GAP_MS = 250;
export const CHRONIC_RUN = 3;
/** A run of this many consecutive samples all far below the typical arrival
 *  and flat (not falling) is a level shift — the path itself got slower —
 *  rather than jitter: forget the old window and re-level at once. */
const LEVEL_RUN = 4;
const LEVEL_SPREAD = 8;
const MIN_SAMPLES = 2;

/** First guess at the lead, used once when the race's first snapshot arrives
 *  (there is no feedback yet): a full round trip (the snapshot is ~RTT/2 old
 *  and the input needs ~RTT/2 to arrive) plus a starting buffer. The
 *  controller corrects it from the first samples. */
export function initialLeadTicks(rttMs: number | null, dtMs: number): number {
  const rtt = rttMs ?? 100; // no ping sample yet — assume a typical mobile link
  return clamp(Math.ceil(rtt / dtMs) + SAFE_MARGIN + 6, SAFE_MARGIN + 6, 72);
}

export type ClockAction =
  | { kind: 'advance'; ticks: number } // simulate this many extra ticks now
  | { kind: 'skip' } // do not simulate a tick this turn
  | null;

interface Sample {
  at: number;
  /** The margin with every clock adjustment made so far taken OUT, so samples
   *  from before and after an adjustment stay comparable. */
  base: number;
}

export class ClockSync {
  /** Net ticks this controller has moved the clock since the seat. */
  private adj = 0;
  /** Samples whose interval began before the server had seen this label
   *  predate the last clock change and are ignored. */
  private holdLabel = -Infinity;
  private win: Sample[] = [];
  private recent: number[] = [];
  private cooldown = 0;
  /** Cached window statistics (recomputed per sample — 20×/s, not per tick). */
  private worstBase: number | null = null;
  private typBase = 0;
  private lastErr = 0;
  /** When the last local stall was reported, and how many came close together. */
  private lastStallAt = -Infinity;
  private stallRun = 0;

  private restat(nowMs: number): void {
    while (this.win.length && nowMs - this.win[0].at > WINDOW_MS) this.win.shift();
    if (this.win.length < MIN_SAMPLES) {
      this.worstBase = null;
      return;
    }
    const sorted = this.win.map((s) => s.base).sort((a, b) => a - b);
    // "Typical" = the 80th percentile: the unhindered path, robust to one odd
    // high reading (a server hiccup makes inputs look early for one sample).
    this.typBase = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.8))];
    // The worst arrival — but never chased further than JITTER_CAP below the
    // typical one: beyond that it was an outage, and an outage is not jitter.
    this.worstBase = Math.max(sorted[0], this.typBase - JITTER_CAP);
  }

  /** The worst + typical recent arrival in TODAY's clock (diagnostics), or
   *  null before there is feedback. */
  estimate(): { worst: number; typical: number } | null {
    if (this.worstBase === null) return null;
    return { worst: this.worstBase + this.adj, typical: this.typBase + this.adj };
  }

  /**
   * Feed one margin from a LIVE snapshot (`margin` = the least the newest
   * input was ahead over the snapshot interval just ended). Feedback from
   * before our last clock change is dropped: the sample counts only if the
   * server had ALREADY seen a post-change label when its interval began.
   * Returns whether the sample was used.
   */
  sample(margin: number, snapTick: number, nowMs: number): boolean {
    if (snapTick + margin - SNAPSHOT_INTERVAL_TICKS < this.holdLabel) return false;
    const base = margin - this.adj;
    this.win.push({ at: nowMs, base });
    this.recent.push(base);
    if (this.recent.length > LEVEL_RUN) this.recent.shift();
    if (this.recent.length === LEVEL_RUN && this.win.length > LEVEL_RUN) {
      const older = this.win
        .slice(0, -LEVEL_RUN)
        .map((s) => s.base)
        .sort((a, b) => a - b);
      const typ = older[Math.min(older.length - 1, Math.floor(older.length * 0.8))];
      const hi = Math.max(...this.recent);
      const lo = Math.min(...this.recent);
      if (hi < typ - JITTER_CAP && hi - lo <= LEVEL_SPREAD) {
        // The path itself changed: every recent arrival is far later than it
        // used to be, and steadily so. The old window no longer describes it.
        this.win = this.win.slice(-LEVEL_RUN);
      }
    }
    this.restat(nowMs);
    return true;
  }

  /** Ask once per client tick. The caller performs the action, then reports
   *  it with {@link applied}. */
  decide(): ClockAction {
    if (this.cooldown > 0) this.cooldown--;
    if (this.worstBase === null) return null;
    const err = SAFE_MARGIN - (this.worstBase + this.adj); // + = inputs cut it close / late
    this.lastErr = err;
    if (err >= 1) return { kind: 'advance', ticks: Math.min(Math.ceil(err), MAX_ADVANCE) };
    if (err <= -RELEASE_HYSTERESIS && this.cooldown === 0) return { kind: 'skip' };
    return null;
  }

  /** The caller did what {@link decide} asked. `labelAfter` = the label the
   *  next generated input will carry. */
  applied(action: Exclude<ClockAction, null>, labelAfter: number): void {
    if (action.kind === 'advance') {
      this.adj += action.ticks;
    } else {
      this.adj -= 1;
      this.cooldown = this.lastErr <= -FAR_AHEAD ? RELEASE_COOLDOWN_FAR : RELEASE_COOLDOWN;
    }
    this.holdLabel = labelAfter;
  }

  /** The client's clock moved for a reason of its OWN (it caught up after a
   *  device freeze): the lead is unchanged, but every sample measured while
   *  it was behind is contaminated — ignore feedback until the server has
   *  seen a label from after the catch-up. */
  jumped(labelAfter: number): void {
    this.holdLabel = labelAfter;
  }

  /**
   * The render loop lost time and the driver has made it up (M143c).
   *
   * An ISOLATED stall (one long frame — a shader compile, a garbage
   * collection) says nothing about the link: the "late" readings it produced
   * are ignored, exactly like {@link jumped}. So is a `big` one (a hidden
   * tab, a long freeze), always.
   *
   * A CHRONIC one is different. A device that cannot hold ~15 frames a second
   * loses a little time on EVERY frame, so it reports a stall every frame —
   * and ignoring feedback after each one meant ignoring all of it, for ever:
   * measured, 0 of ~1,000 margins accepted at 12 frames a second. The buffer
   * never adapted on exactly the devices whose bursty sending needs it most.
   * From the CHRONIC_RUN-th stall in a row the readings are kept: they are
   * how this device really sends, and the buffer should cover it.
   *
   * Returns whether the feedback is being held back.
   */
  stalled(labelAfter: number, nowMs: number, big: boolean): boolean {
    this.stallRun = nowMs - this.lastStallAt <= CHRONIC_GAP_MS ? this.stallRun + 1 : 0;
    this.lastStallAt = nowMs;
    if (!big && this.stallRun >= CHRONIC_RUN) return false;
    this.holdLabel = labelAfter;
    return true;
  }

  /** Net adjustment so far (diagnostics). */
  get adjustment(): number {
    return this.adj;
  }
}
